import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Article {
  slug: string;
  title: string;
  subtitle: string;
  category: string;
  author: string;
  published_at: string;
  tags: string[];
}

function ArticlesPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await fetch('/api/articles');
      if (!response.ok) throw new Error('Greška pri dohvaćanju članaka');
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nepoznata greška');
      // Fallback - hardcoded articles for now
      setArticles([
        {
          slug: 'svedski-standard-s-teksaskim-porezima',
          title: 'Švedski standard s teksaškim porezima',
          subtitle: 'Hrvati žele skandinavske usluge, a plaćaju poreze kao Amerikanci.',
          category: 'proracun-i-porezi',
          author: 'Igor Grčman',
          published_at: '2025-12-18',
          tags: ['porezi', 'javne-usluge']
        },
        {
          slug: 'stan-nije-bankomat',
          title: 'Stan nije bankomat',
          subtitle: 'Zašto tvoj stan ne bi trebao financirati tvoju mirovinu.',
          category: 'stanovanje-i-zivot',
          author: 'Igor Grčman',
          published_at: '2025-12-18',
          tags: ['stanovanje', 'mirovine']
        },
        {
          slug: 'tko-placa-tvoje-dijete',
          title: 'Tko plaća tvoje dijete?',
          subtitle: 'Ekonomija roditeljstva koju nitko ne želi čuti.',
          category: 'javne-usluge',
          author: 'Igor Grčman',
          published_at: '2025-12-18',
          tags: ['djeca', 'obrazovanje']
        },
        {
          slug: 'pogled-iz-bogote',
          title: 'Pogled iz Bogote: Što kad država prestane lagati da smo svi isti?',
          subtitle: 'U Hrvatskoj se pravimo da klase ne postoje. U Bogoti vam na računu za struju piše točno tko ste.',
          category: 'digitalna-pismenost',
          author: 'Igor Grčman',
          published_at: '2025-12-18',
          tags: ['estratos', 'klase']
        },
        {
          slug: 'tko-je-ukrao-bozic',
          title: 'Tko je ukrao Božić? (Nije Grinch, nego Excel tablica)',
          subtitle: 'Advent u Zagrebu više ne blješti kao nekad. I to je najbolja vijest koju ste mogli čuti.',
          category: 'javne-usluge',
          author: 'Igor Grčman',
          published_at: '2025-12-18',
          tags: ['advent', 'zagreb']
        },
        {
          slug: 'slucajnost-od-54000-eura',
          title: '"Slučajnost" od 54.000 eura: Kako izgleda zakon za elitu',
          subtitle: 'Martina Dalić prekršila Zakon o tržištu kapitala. Kazna? Javno upozorenje.',
          category: 'javne-usluge',
          author: 'Igor Grčman',
          published_at: '2025-12-18',
          tags: ['hanfa', 'dalic']
        },
        {
          slug: 'spijun-u-dzepu-banka-kazna',
          title: 'Špijun u džepu: Banka ti je napravila digitalni pretres',
          subtitle: '1,5 milijuna eura kazne jer su znali sve o tebi. Tvoja cijena? 3,46 eura.',
          category: 'digitalna-pismenost',
          author: 'Igor Grčman',
          published_at: '2025-12-18',
          tags: ['azop', 'privatnost']
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'Svi članci' },
    { id: 'proracun-i-porezi', name: 'Proračun i porezi' },
    { id: 'javne-usluge', name: 'Javne usluge' },
    { id: 'stanovanje-i-zivot', name: 'Stanovanje i život' },
    { id: 'digitalna-pismenost', name: 'Digitalna pismenost' }
  ];

  const filteredArticles = selectedCategory === 'all' 
    ? articles 
    : articles.filter(a => a.category === selectedCategory);

  const getCategoryLabel = (category: string) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.name : category;
  };

  if (loading) {
    return (
      <div className="articles-page">
        <div className="loading">Učitavam članke...</div>
      </div>
    );
  }

  return (
    <div className="articles-page">
      <header className="articles-header">
        <h1>Članci</h1>
        <p className="articles-intro">
          Analiza hrvatskih ekonomskih tema. Bez emocija, samo matematika.
        </p>
      </header>

      <div className="articles-filters">
        {categories.map(cat => (
          <button
            key={cat.id}
            className={`filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.id)}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {error && <div className="error-banner">⚠️ {error}</div>}

      <div className="articles-grid">
        {filteredArticles.map(article => (
          <article key={article.slug} className="article-card">
            <div className="article-card-category">
              {getCategoryLabel(article.category)}
            </div>
            <h2 className="article-card-title">
              <Link to={`/clanak/${article.slug}`}>{article.title}</Link>
            </h2>
            <p className="article-card-subtitle">{article.subtitle}</p>
            <div className="article-card-meta">
              <span className="article-author">{article.author}</span>
              <span className="article-date">
                {new Date(article.published_at).toLocaleDateString('hr-HR')}
              </span>
            </div>
            <div className="article-card-tags">
              {article.tags?.slice(0, 3).map(tag => (
                <span key={tag} className="tag">#{tag}</span>
              ))}
            </div>
            <Link to={`/clanak/${article.slug}`} className="article-card-link">
              Čitaj više →
            </Link>
          </article>
        ))}
      </div>

      {filteredArticles.length === 0 && (
        <div className="no-articles">
          <p>Nema članaka u ovoj kategoriji.</p>
        </div>
      )}
    </div>
  );
}

export default ArticlesPage;
